import { Component, ElementRef, HostListener, OnInit, Renderer2, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UsersService } from 'src/app/users/users.service';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { Subscription } from 'rxjs';
import { OrdersService } from '../orders.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import validationRulesModel from '../../../../../../iotah-common/validations/validationRules.json';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { CommonService } from 'src/app/shared/services/common.service';

@Component({
	selector: 'app-edit-order',
	templateUrl: './edit-order.component.html',
})
export class EditOrderComponent implements OnInit {

	@ViewChild("editOrderForm") editOrderForm: NgForm;
	routeSubscription!: Subscription;
	action!: string;

	allowedActions: string[] = ['add', 'edit', 'view'];
	formFields: any = [];

	order: any = {
		iotah_count: 0,
		charglink_count: 0,
		study_count: 0,
		battlink_count: 0,
		brm_count: 0,
		battlink_lithium_count: 0,
		battlink_ii_count: 0,
		sp1_count: 0,
		sp3_count: 0,
		iotemeter_count: 0,
		iotemeter_edge_count: 0
	};
	orderId: number = 0;

	validationRules: any = {};
	invalidFields: any = {};

	pricesOptions: any = [];
	salesChannelOptions: any = [];
	subscriptionTermsOptions: any = [];
	warrantyPeriodOptions = [
		{value: 1, label: `1 ${this.translateService.instant("year")}`},
		{value: 2, label: `2 ${this.translateService.instant("years")}`},
		{value: 3, label: `3 ${this.translateService.instant("years")}`},
		{value: 4, label: `4 ${this.translateService.instant("years")}`},
		{value: 5, label: `5 ${this.translateService.instant("years")}`},
	];
	defaultWarrantyPeriod = 1;

	notificationEmails: {email: string}[] = [];
	poFieldClasses: string = 'form-control';
	fieldsToTrim: string[] = [
		'receiver_company_email',
		'receiver_email',
		'notifications_email_list',
		'po_number',
		'shipment_tracking_number',
		'receiver_name',
		'receiver_company_name',
		'receiver_first_address_line',
		'receiver_second_address_line',
		'receiver_postal_code',
		'receiver_city',
		'receiver_country',
		'reseller',
	];

	constructor(
		private userService: UsersService,
		private router: Router,
		private route: ActivatedRoute,
		public notificationMessage: NotificationMessageService,
		public ordersService: OrdersService,
		private sideMenuService: SideMenuService,
		private navigation: NavigationService,
		private translateService: TranslateService,
		private el: ElementRef,
		private renderer: Renderer2,
		private commonService: CommonService
	) { }

	ngOnInit(): void {
		this.sideMenuService.hide();

		this.ordersService.shouldBackToSelectedTab = false;

		this.routeSubscription = this.route.params.subscribe((params: any) => {
			this.action = params.action || null;

			if (!params || !this.allowedActions.includes(this.action) || (['edit', 'view'].includes(this.action) && !params.id) || (this.action == 'add' && params.id))
				return this.router.navigateByUrl('/404');

			const access = this.userService.hasAccessFunction('create_orders') || this.userService.hasAccessFunction('view_orders') || this.userService.hasAccessFunction('shipment_management');
			if (!access)
				this.router.navigate(['/unauthorized']);

			this.orderId = +params.id || 0;

			if (this.action != 'add')
				this.getOrder();

			this.validationRules = validationRulesModel.orders;
			this.pricesOptions = this.ordersService.getPricesOptions();
			this.salesChannelOptions = this.ordersService.getSalesChannelOptions();
			this.subscriptionTermsOptions = this.ordersService.getSubscriptionTermsOptions();
			this.order.subscription_terms = this.subscriptionTermsOptions[0].value;
			this.order.warranty_years = this.order.warranty_years || this.defaultWarrantyPeriod;
		});
	}

	isEditForm() {
		return this.action == 'edit';
	}

	isFormDisabled() {
		return this.action == 'view';
	}

	getOrder() {
		this.ordersService.getOrder(this.orderId).subscribe((order: any) => {
			if(this.isEditForm() && order.status === +(this.ordersService.getStatusesByKey()).SHIPPED)
				return this.router.navigateByUrl('/404');

			const poNumber = order.po_number;
			const title = this.isEditForm() ? this.translateService.instant('orders.page_title_edit', { poNumber }) : this.translateService.instant('orders.page_title_view', { poNumber });
			this.commonService.setPageTabTitle(title);
			this.notificationEmails = order.notifications_email_list.trim().split(' ').map((email: string) => { return { email }});
			this.order = order;
			this.order.calculated_shipping_date = this.order.calculated_shipping_date ? moment.unix(this.order.calculated_shipping_date).utc().format('MM/DD/YYYY') : '-'
		});
	}

	customValidation(formFields: any) {
		if (!(formFields.iotah_count + formFields.charglink_count + formFields.study_count + formFields.battlink_count + formFields.brm_count + formFields.battlink_lithium_count + formFields.battlink_ii_count + formFields.sp1_count + formFields.sp3_count + formFields.iotemeter_count + formFields.iotemeter_edge_count)) {
			this.highlightInvalidField('iotah_count');
			this.highlightInvalidField('charglink_count');
			this.highlightInvalidField('study_count');
			this.highlightInvalidField('battlink_count');
			this.highlightInvalidField('battlink_lithium_count');
			this.highlightInvalidField('battlink_ii_count');
			this.highlightInvalidField('brm_count');
			this.highlightInvalidField('sp1_count');
			this.highlightInvalidField('sp3_count');
			this.highlightInvalidField('iotemeter_count');
			this.highlightInvalidField('iotemeter_edge_count');
			this.notificationMessage.setMessage('translate|orders.order_must_contain_at_least_one_device', {clearOnXTimeNavigate: 1});
			return false;
		}
		return true;
	}

	saveOrder() {
		let formFields = this.editOrderForm.value;
		formFields.id = this.orderId;

		for (const key in formFields) {
			if (this.fieldsToTrim.includes(key) && formFields[key]) {
				if (key == 'notifications_email_list')
					formFields.notifications_email_list = formFields.notifications_email_list.map(item => ({ email: item.email.trim() }));
				else
					formFields[key] = formFields[key].trim();
			}
		}

		if(!this.customValidation(formFields))
			return;

		this.ordersService.saveOrder(formFields).subscribe((res: any) => {
			switch (res.api_status) {
				case 2:
					this.notificationMessage.setMessage('translate|g.invalid_fields', {clearOnXTimeNavigate: 1});
					break;
				case 3:
					this.poFieldClasses = this.poFieldClasses.concat(' invalid-input');
					this.notificationMessage.setMessage('translate|orders.duplicate_po_number', {timeout: 20000, clearOnXTimeNavigate: 1});
					break;
				case 4:
					this.highlightInvalidField('iotah_count');
					this.notificationMessage.setMessage('translate|orders.cannot_decrease_the_iotah_order_count', {clearOnXTimeNavigate: 1});
					break;
				case 5:
					this.highlightInvalidField('charglink_count');
					this.notificationMessage.setMessage('translate|orders.cannot_decrease_the_charglink_order_count', {clearOnXTimeNavigate: 1});
					break;
				case 6:
					this.highlightInvalidField('study_count');
					this.notificationMessage.setMessage('translate|orders.cannot_decrease_the_study_order_count', {clearOnXTimeNavigate: 1});
					break;
				case 7:
					this.highlightInvalidField('battlink_count');
					this.notificationMessage.setMessage('translate|orders.cannot_decrease_the_battlink_order_count', {clearOnXTimeNavigate: 1});
					break;
				case 8:
					this.highlightInvalidField('brm_count');
					this.notificationMessage.setMessage('translate|orders.cannot_decrease_the_brm_order_count', {clearOnXTimeNavigate: 1});
					break;
				case 9:
					this.highlightInvalidField('battlink_lithium_count');
					this.notificationMessage.setMessage('translate|orders.cannot_decrease_the_battlink_lithium_order_count', {clearOnXTimeNavigate: 1});
					break;
				case 10:
					this.highlightInvalidField('battlink_ii_count');
					this.notificationMessage.setMessage('translate|orders.cannot_decrease_the_battlink_ii_order_count', {clearOnXTimeNavigate: 1});
					break;
				case 11:
					this.highlightInvalidField('sp1_count');
					this.notificationMessage.setMessage('translate|orders.cannot_decrease_the_sp1_order_count', {clearOnXTimeNavigate: 1});
					break;
				case 12:
					this.highlightInvalidField('sp3_count');
					this.notificationMessage.setMessage('translate|orders.cannot_decrease_the_sp3_order_count', {clearOnXTimeNavigate: 1});
					break;
				case 13:
					this.highlightInvalidField('iotemeter_edge_count');
					this.notificationMessage.setMessage('translate|orders.cannot_decrease_the_iotemeter_edge_order_count', {clearOnXTimeNavigate: 1});
					break;
				case 14:
					this.highlightInvalidField('iotemeter_count');
					this.notificationMessage.setMessage('translate|orders.cannot_decrease_the_iotemeter_order_count', {clearOnXTimeNavigate: 1});
					break;
				default:
					this.notificationMessage.setMessage('globalSuccessMsg', {clearOnXTimeNavigate: 1});
					this.goBack();
					break;
			}
		});
	}

	highlightInvalidField(fieldName: string) {
		const form = this.el.nativeElement;
		const element = form.querySelectorAll(`[name=${fieldName}]`);
		if (element && element[0])
			this.renderer.addClass(element[0], 'invalid-input');
	}

	shipmentTrackingFieldsVisibility(status: number) {
		return status === +(this.ordersService.getStatusesByKey()).SHIPPED && this.isFormDisabled;
	}

	isPoNumberDisabled(status: number) {
		return this.isFormDisabled() ||
			(this.isEditForm() && (status != +(this.ordersService.getStatusesByKey()).NEW || !this.userService.hasAccessFunction('create_orders')));
	}

	@HostListener('window:popstate', ['$event'])
	onPopState() {
		this.ordersService.shouldBackToSelectedTab = true;
	}

	goBack() {
		this.ordersService.shouldBackToSelectedTab = true;
		this.navigation.goBack('/orders/manage');
	}
}
